import { z } from 'zod';

/**
 * A finished heist as the meta game sees it: who sat where, what they were, and how it ended. The
 * server builds it from the engine's result; `settleMatch` turns it into what changes for each
 * player; the database stores both. Only accounts appear in `players` (the bots do not).
 */

/** The two ratings of a player: as a thief and as the infiltrator (GDD §14). */
export type RatingKind = 'thief' | 'infiltrator';

export const MATCH_MODES = ['quick', 'private', 'ranked', 'daily'] as const;
export type MatchMode = (typeof MATCH_MODES)[number];

const ROLES = ['mastermind', 'locksmith', 'hacker', 'lookout', 'driver'] as const;
const MAPS = ['bank', 'museum', 'villa', 'express'] as const;
const DIFFICULTIES = ['easy', 'normal', 'pro', 'legend'] as const;

export const MatchPlayerSchema = z.strictObject({
  /** The account (`auth.users.id`). */
  userId: z.uuid(),
  /** The seat in the heist: `p1`…`p5`. */
  seat: z.string().min(1).max(16),
  role: z.enum(ROLES),
  infiltrator: z.boolean(),
  won: z.boolean(),
  /** Voted out at the hideout. */
  expelled: z.boolean(),
  /** The share of the loot, or the infiltrator's score, in millions of lire (0 for the losers). */
  earned: z.number().int().nonnegative(),
  /** What the player's secret accusation named: the real infiltrator, somebody else, or nobody. */
  accused: z.enum(['right', 'wrong', 'none']),
  /** Left for good, or never came back to the seat before the end. */
  abandoned: z.boolean(),
});
export type MatchPlayer = z.infer<typeof MatchPlayerSchema>;

export const MatchRecordSchema = z.strictObject({
  /** A UUID the server makes: the match's name everywhere. */
  id: z.uuid(),
  mode: z.enum(MATCH_MODES),
  mapId: z.enum(MAPS),
  difficulty: z.enum(DIFFICULTIES),
  /** Seats at the table, bots included. */
  seats: z.number().int().min(3).max(5),
  hadInfiltrator: z.boolean(),
  success: z.boolean(),
  failure: z.enum(['alarm', 'time', 'vault']).nullable(),
  /** Loot after the alarm penalty, in millions of lire. */
  loot: z.number().int().nonnegative(),
  /** The alarm at the end, in tenths of a percent point (0–1000). */
  alarmFinal: z.number().int().min(0).max(1000),
  /** How long the game ran, in milliseconds of game time. */
  durationMs: z.number().int().nonnegative(),
  endedAtMs: z.number().int().nonnegative(),
  rulesVersion: z.number().int().positive(),
  /** The `dayKey` of a daily heist, otherwise `null`. */
  dailyKey: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable(),
  /** Which of the day's attempts a daily heist was (1–3). */
  dailyAttempt: z.number().int().min(1).max(10).nullable().optional(),
  players: z.array(MatchPlayerSchema).min(1).max(5),
});
export type MatchRecord = z.infer<typeof MatchRecordSchema>;

/** The rap sheet and the experience of a player (GDD §15). */
export const StatsSchema = z.object({
  xp: z.number().int().nonnegative().default(0),
  matches: z.number().int().nonnegative().default(0),
  /** Heists that were a success, whoever won. */
  heists: z.number().int().nonnegative().default(0),
  wins: z.number().int().nonnegative().default(0),
  /** Total loot taken home, in millions of lire. */
  loot: z.number().int().nonnegative().default(0),
  bestLoot: z.number().int().nonnegative().default(0),
  asInfiltrator: z.number().int().nonnegative().default(0),
  infiltratorWins: z.number().int().nonnegative().default(0),
  unmasksRight: z.number().int().nonnegative().default(0),
  unmasksWrong: z.number().int().nonnegative().default(0),
  abandoned: z.number().int().nonnegative().default(0),
  dailyRuns: z.number().int().nonnegative().default(0),
});
export type Stats = z.infer<typeof StatsSchema>;

export const EMPTY_STATS: Stats = StatsSchema.parse({});

/** What one match adds to the rap sheet (`bestLoot` is a maximum, the rest are sums). */
export type StatsDelta = Omit<Stats, 'xp'>;
